import { useEffect, useRef } from 'react';
import { pad2 } from './clockUtils';

const ITEM_H = 48;

// Scroll-snap number wheel (Samsung-style): centre row is the selected value.
export const WheelColumn = ({ label, max, value, onChange, testId }) => {
  const ref = useRef(null);
  const settleRef = useRef(null);

  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    const el = ref.current;
    if (el && Math.round(el.scrollTop / ITEM_H) !== value) {
      el.scrollTo({ top: value * ITEM_H, behavior: 'smooth' });
    }
  }, [value]);

  useEffect(() => () => clearTimeout(settleRef.current), []);

  // Re-align instantly whenever the wheel becomes visible (e.g. tab switch).
  useEffect(() => {
    const el = ref.current;
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) el.scrollTop = valueRef.current * ITEM_H;
    });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const handleScroll = () => {
    clearTimeout(settleRef.current);
    settleRef.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.min(max, Math.max(0, Math.round(ref.current.scrollTop / ITEM_H)));
      if (idx !== value) onChange(idx);
    }, 90);
  };

  return (
    <div className="flex flex-col items-center flex-1 min-w-0">
      <span className="text-[11px] uppercase tracking-[0.2em] text-white/40 mb-2">{label}</span>
      <div className="relative w-full" style={{ height: ITEM_H * 3 }}>
        <div
          ref={ref}
          onScroll={handleScroll}
          className="h-full overflow-y-scroll snap-y snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          style={{ paddingTop: ITEM_H, paddingBottom: ITEM_H, maskImage: 'linear-gradient(to bottom, transparent, black 30%, black 70%, transparent)' }}
          data-testid={testId}
        >
          {Array.from({ length: max + 1 }).map((_, n) => (
            <button
              type="button"
              key={n}
              onClick={() => onChange(n)}
              className={`snap-center w-full flex items-center justify-center tabular-nums font-bold transition-[color,transform] duration-200 ${
                n === value ? 'text-primary text-[32px] sm:text-[38px] scale-100' : 'text-white/25 text-[26px] sm:text-[30px] scale-90'
              }`}
              style={{ height: ITEM_H }}
              data-testid={`${testId}-${n}`}
            >
              {pad2(n)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default WheelColumn;
